/**
 * Strip inline pinyin annotations that immediately follow CJK characters
 * (e.g. 咚Dōnɡ鏘qiānɡ → 咚鏘) so TTS does not read both the character and its
 * phonetic spelling.
 */

/** Precomposed tone vowels + ü forms + pinyin script-g (U+0261). */
const PINYIN_MARK = /[āáǎàēéěèīíǐìōóǒòūúǔùǖǘǚǜĀÁǍÀĒÉĚÈĪÍǏÌŌÓǑÒŪÚǓÙǕǗǙǛüÜɡ]/;

/**
 * Common Mandarin syllable shape (initial + final), optional tone digit 1–5.
 * Covers zh/ch/sh/ng digraphs; case-insensitive via flag on caller.
 */
const PINYIN_SYLLABLE =
  /^(?:b|p|m|f|d|t|n|l|g|k|h|j|q|x|zh|ch|sh|r|z|c|s|y|w|ng)?(?:a|ai|an|ang|ao|e|ei|en|eng|er|i|ia|ian|iang|iao|ie|in|ing|iong|iu|o|ong|ou|u|ua|uai|uan|uang|ue|ui|un|uo|ü|üe|v|ve)[1-5]?$/i;

/** Latin letter run that may be a pinyin syllable (incl. tone marks / ɡ). */
const LATIN_RUN = /[A-Za-z\u00C0-\u024F\u1E00-\u1EFF\u0250-\u02AF]+/;

/** CJK Unified Ideographs + Extension A. */
const CJK = /[\u3400-\u4DBF\u4E00-\u9FFF]/;

const CJK_THEN_LATIN = new RegExp(
  `(${CJK.source})(${LATIN_RUN.source})`,
  "gu"
);

function isAllCapsAcronym(latin) {
  return /^[A-Z]{2,}$/.test(latin);
}

function isPinyinLike(latin) {
  if (!latin) {
    return false;
  }
  if (PINYIN_MARK.test(latin)) {
    return true;
  }
  // Normalize ü/ɡ for plain-syllable check
  const normalized = latin.replace(/ɡ/g, "g").replace(/ü/gi, "v");
  return PINYIN_SYLLABLE.test(normalized);
}

/**
 * Remove pinyin-like Latin runs glued to CJK characters.
 * Keeps all-caps acronyms (CPU, AI) and spaced English.
 * @param {string} text
 * @returns {string}
 */
export function stripInlinePinyin(text) {
  if (!text) {
    return text || "";
  }

  return text.replace(CJK_THEN_LATIN, (match, cjk, latin) => {
    if (isAllCapsAcronym(latin)) {
      return match;
    }
    if (isPinyinLike(latin)) {
      return cjk;
    }
    return match;
  });
}
